/**
 * IndicadorTooltip — Tooltip descritivo para indicadores dos dashboards
 *
 * Exibe um ícone (?) ao lado do label do indicador. Ao passar o mouse,
 * mostra uma explicação em linguagem simples sobre o que o indicador mede
 * e como interpretar seu valor.
 *
 * Respeita o modo Explicativo/Compacto do UIContext:
 * - Modo Explicativo: exibe o tooltip normalmente
 * - Modo Compacto: oculta o tooltip para reduzir ruído visual
 *
 * Uso:
 *   <IndicadorTooltip indicadorKey="adesao" />
 *   <IndicadorTooltip indicadorKey="owi" side="right" />
 *   <IndicadorTooltip titulo="Meu Indicador" descricao="Explicação..." interpretacao="..." />
 */
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getIndicador, type IndicadorDef } from "@/constants/indicadores";
import { useUI } from "@/contexts/UIContext";

type TooltipSide = "top" | "bottom" | "left" | "right";

interface IndicadorTooltipProps {
  /** Chave do indicador no glossário (indicadores.ts) */
  indicadorKey?: string;
  /** Título customizado (sobrescreve o do glossário) */
  titulo?: string;
  /** Descrição customizada (sobrescreve a do glossário) */
  descricao?: string;
  /** Interpretação customizada (sobrescreve a do glossário) */
  interpretacao?: string;
  /** Contexto adicional (opcional) */
  contexto?: string;
  /** Faixa de valores (opcional) */
  faixa?: string;
  /** Lado em que o tooltip aparece */
  side?: TooltipSide;
  /** Tamanho do ícone */
  size?: number;
  /** Classes CSS adicionais */
  className?: string;
  /**
   * Se true, exibe o tooltip mesmo no modo Compacto.
   * Use para tooltips críticos que sempre devem ser visíveis.
   */
  forceVisible?: boolean;
}

export default function IndicadorTooltip({
  indicadorKey,
  titulo,
  descricao,
  interpretacao,
  contexto,
  faixa,
  side = "top",
  size = 14,
  className = "",
  forceVisible = false,
}: IndicadorTooltipProps) {
  // Verifica modo compacto/explicativo
  const { isExplicativo } = useUI();

  // No modo compacto, não exibe o tooltip (a menos que forceVisible seja true)
  if (!isExplicativo && !forceVisible) return null;

  // Busca definição do glossário se indicadorKey foi fornecido
  const def: IndicadorDef | undefined = indicadorKey ? getIndicador(indicadorKey) : undefined;

  // Mescla: props explícitas têm prioridade sobre o glossário
  const resolvedTitulo = titulo ?? def?.titulo;
  const resolvedDescricao = descricao ?? def?.descricao;
  const resolvedInterpretacao = interpretacao ?? def?.interpretacao;
  const resolvedContexto = contexto ?? def?.contexto;
  const resolvedFaixa = faixa ?? def?.faixa;

  // Não renderiza se não há conteúdo para exibir
  if (!resolvedDescricao && !resolvedInterpretacao) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors focus:outline-none ${className}`}
            aria-label={resolvedTitulo ? `Informações sobre ${resolvedTitulo}` : "Informações sobre este indicador"}
          >
            <HelpCircle size={size} className="shrink-0" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className="max-w-sm p-0 overflow-hidden"
          sideOffset={6}
        >
          <div className="bg-popover text-popover-foreground">
            {/* Cabeçalho com título */}
            {resolvedTitulo && (
              <div className="px-3 py-2 border-b bg-muted/50">
                <p className="text-xs font-semibold text-foreground">{resolvedTitulo}</p>
              </div>
            )}

            <div className="px-3 py-2 space-y-2">
              {/* Descrição principal */}
              {resolvedDescricao && (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {resolvedDescricao}
                </p>
              )}

              {/* Como interpretar */}
              {resolvedInterpretacao && (
                <div className="bg-blue-50 dark:bg-blue-950/30 rounded px-2 py-1.5">
                  <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-0.5">
                    Como interpretar
                  </p>
                  <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                    {resolvedInterpretacao}
                  </p>
                </div>
              )}

              {/* Contexto adicional */}
              {resolvedContexto && (
                <p className="text-[10px] text-muted-foreground italic leading-relaxed border-t pt-1.5">
                  {resolvedContexto}
                </p>
              )}

              {/* Faixa de valores */}
              {resolvedFaixa && (
                <p className="text-[10px] text-muted-foreground">
                  <span className="font-medium">Escala:</span> {resolvedFaixa}
                </p>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
